import React, { useCallback, useState } from "react";
import { AdditionalAssetMeta, Project } from "@amzn/afxtaco-types";
import { logger } from "@amzn/afxtaco-utilities";
import StatusStrip from "./StatusStrip";
import ProjectStateCallout from "./ProjectStateCallout";
import DeliverablesCard from "./DeliverablesCard";
import DeliveryChecklistCard from "./DeliveryChecklistCard";
import UploadedFilesCard from "./UploadedFilesCard";
import LinkAccountDialog from "./LinkAccountDialog";
import { useProjects } from "../../hooks/useProjects";
import { useOverviewData } from "./hooks/useOverviewData";
import type { CanonicalDeliverableSpec } from "./utils/deliverables";
import {
  buildDeliverableRequest,
  type DeliveryItem as DeliveryItemData,
} from "./utils/deliveryChecklist";
import {
  readCustomerContext,
  type CustomerContextInput,
} from "@/utils/customerContext";

interface OverviewTabProps {
  projectId: string | undefined;
  project: Project | undefined;
  /** Canonical artifacts to show, with their tab values (feature-flag aware). */
  canonicalSpecs: CanonicalDeliverableSpec[];
  /** Already fetched by the page — passed in rather than re-requested. */
  additionalAssets: AdditionalAssetMeta[];
  additionalLabel: (filename: string) => string;
  additionalTabValue: (path: string) => string;
  /** `summary.md` contents, already fetched by the page. */
  summaryContent: string | undefined;
  /**
   * Contents of the structured delivery-definition asset, already fetched by the
   * page. Preferred over parsing the checklist out of the summary prose.
   */
  deliveryDefinitionContent: string | undefined;
  /** Activates another tab, for deliverable click-through. */
  onOpenTab: (tabValue: string) => void;
  /**
   * Populates the chat input with `text` and focuses it. Never sends — the user
   * reviews and submits. Pass an empty string to just focus.
   */
  onPrefillChat: (text: string) => void;
  /** Skip fetching (the new-project flow, before anything exists in S3). */
  skip?: boolean;
}

/**
 * The project Overview tab: where the project stands and what TACO has produced.
 *
 * This is the only component in the feature that touches hooks or Redux —
 * everything it renders is a pure presentational child taking props, which keeps
 * those children testable with a plain `render` and keeps this file thin.
 *
 * Note there is deliberately no page-level spinner. Blocking a comprehension
 * page on a fetch defeats its purpose, so each card handles its own loading and
 * empty states, and a failed request degrades that card alone.
 */
const OverviewTab: React.FC<OverviewTabProps> = ({
  projectId,
  project,
  canonicalSpecs,
  additionalAssets,
  additionalLabel,
  additionalTabValue,
  summaryContent,
  deliveryDefinitionContent,
  onOpenTab,
  onPrefillChat,
  skip = false,
}) => {
  const {
    deliverables,
    deliverableCount,
    changedTodayCount,
    isLoadingDeliverables,
    deliveryItems,
    uploadedFiles,
    isLoadingUploads,
    projectState,
  } = useOverviewData({
    projectId,
    canonicalSpecs,
    additionalAssets,
    additionalLabel,
    additionalTabValue,
    summaryContent,
    deliveryDefinitionContent,
    // The Overview's only source for "when did TACO last run".
    sessionLastActivityAt: project?.latestSessionLastActivityAt,
    skip,
  });

  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkError, setLinkError] = useState<string | undefined>();
  const { updateProject } = useProjects();

  // Seeds the dialog so editing starts from what is recorded. First entry only:
  // the form has one field per entity, and a project has one customer — extra
  // IDs only exist when the agent recorded them, and are shown in the box above.
  const recordedCustomer = readCustomerContext(project);

  // Clears any previous failure on the way in, so a retry does not open onto the
  // error from the last attempt.
  const openCustomerDialog = useCallback(() => {
    setLinkError(undefined);
    setIsLinkDialogOpen(true);
  }, []);

  // Asks TACO for a deliverable, carrying the purpose the agent itself wrote so
  // the request states the quality bar and not just the artifact's name.
  const handleAsk = useCallback(
    (item: DeliveryItemData) => {
      onPrefillChat(buildDeliverableRequest(item));
    },
    [onPrefillChat],
  );

  // Saves the IDs onto the project rather than asking TACO for them in chat.
  // They become the project's customer context immediately — the Customer box
  // updates from Redux, and every subsequent turn carries them to the agent, so
  // there is nothing for the user to send.
  const handleLinkCustomer = useCallback(
    async (input: CustomerContextInput): Promise<boolean> => {
      if (!projectId) return false;
      setLinkError(undefined);
      try {
        await updateProject(projectId, {
          ...(input.accountId.trim()
            ? { accountId: input.accountId.trim() }
            : {}),
          ...(input.opportunityId.trim()
            ? { opportunityId: input.opportunityId.trim() }
            : {}),
        });
        return true;
      } catch (error) {
        logger.error({
          loggerMsg: "[OverviewTab] Failed to save customer context",
          projectId,
          error,
        });
        setLinkError("Couldn't save that. Please try again.");
        // Reported rather than swallowed, so the dialog stays open showing the
        // error with the user's input intact.
        return false;
      }
    },
    [projectId, updateProject],
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-[1400px] px-6 pt-5 pb-10">
        <StatusStrip
          project={project}
          lastActivityAt={project?.latestSessionLastActivityAt}
          deliverableCount={deliverableCount}
          changedTodayCount={changedTodayCount}
          onEditCustomer={openCustomerDialog}
        />

        <ProjectStateCallout
          state={projectState}
          onStartInChat={() => onPrefillChat("")}
        />

        {/*
          Two columns: everything TACO produced on the left, what it suggests
          producing next on the right. Uploads sit under Deliverables because
          they are the same kind of thing — files this project holds — and the
          shared FileRow makes them read as one continuous inventory.
        */}
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(300px,0.85fr)]">
          <div>
            <DeliverablesCard
              deliverables={deliverables}
              isLoading={isLoadingDeliverables}
              onOpenTab={onOpenTab}
            />
            <UploadedFilesCard
              files={uploadedFiles}
              isLoading={isLoadingUploads}
            />
          </div>

          <div>
            <DeliveryChecklistCard items={deliveryItems} onAsk={handleAsk} />
          </div>
        </div>
      </div>

      <LinkAccountDialog
        open={isLinkDialogOpen}
        onOpenChange={setIsLinkDialogOpen}
        onSubmit={handleLinkCustomer}
        error={linkError}
        currentAccountId={recordedCustomer.accountIds[0]}
        currentOpportunityId={recordedCustomer.opportunityIds[0]}
      />
    </div>
  );
};

export default OverviewTab;
